import {Inject, Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {SITE_CONFIG} from '../../core/site-config.service';
import {CmsObjectHelper} from '../../shared/object-helper.service';
import {AppTemplate} from '../../app-templates/shared/app-templates.service';
import {Observable} from 'rxjs';
import {Pagination} from '../../../types';
import {ISiteConfig} from '../../core/site-config.interface';

export interface AppTemplateSetting {
    id: number;
    key: string;
    type: string | object;
    value: any;
    appTemplate?: AppTemplate;
    updatedAt: string;
    createdAt: string;
}

export interface AppTemplateSettingHistory {
    id: number;
    appTemplateSettingId: number;
    key: string;
    type: string;
    archived: boolean;
    createdAt: string;
    user: string;
    value: any;
}

export interface AppTemplateSettingsResponse {
    appTemplateSettings: Array<AppTemplateSetting>,
    pagination: Pagination
}

export enum SettingType {
    number,
    string,
    boolean,
    object
}

export const SETTINGS_TYPES = [
    {
        id: SettingType[SettingType.number],
        title: 'Number'
    },
    {
        id: SettingType[SettingType.string],
        title: 'String'
    },
    {
        id: SettingType[SettingType.boolean],
        title: 'Boolean'
    },
    {
        id: SettingType[SettingType.object],
        title: 'Object'
    }
];

export const MODULE_ID = 'settings';

/**
 *
 * @param appTemplateSetting
 * @return {AppTemplateSetting}
 */
export function parseAppTemplateSetting (appTemplateSetting: AppTemplateSetting) {
    appTemplateSetting.value = appTemplateSetting.value instanceof Object ?
        JSON.stringify(appTemplateSetting.value) : appTemplateSetting.value;

    return appTemplateSetting;
}

function decodeAppTemplateSetting (appTemplateSetting: {value: any, type: string}) {
    let value = appTemplateSetting.value;

    switch (appTemplateSetting.type) {
        case SettingType[SettingType.boolean]:
            if (value === 'true') {
                value = true;
            } else if (value === 'false') {
                value = false;
            }
            break;
        default:
            break;
    }

    appTemplateSetting.value = value;

}

@Injectable()
export class CmsAppTemplateSettingsService {

    constructor(private http: HttpClient,
                @Inject(SITE_CONFIG) private config: ISiteConfig) {}

    /**
     * Get all app settings
     *
     * @param {Object} params
     * @param {string} params.search - Search apps by key, value, app name or app title
     * @param {string} params.extend - Extend data with related resource information (app)
     * @param {number} params.currentPage - Displayed page number
     * @param {number} params.pageSize - Number of items per page
     * @param {number} params.appTemplateId - id of app template to filter
     * @return {Observable<R>}
     */
    getAppTemplateSettings(params?: Object):
    Observable<{appTemplateSettings: Array<AppTemplateSetting>,
        pagination: Pagination}> {
        return this.http.get<{appTemplateSettings:
            Array<AppTemplateSetting>, pagination: Pagination}>
            (`${this.config.gravityCmsApi}/cms/app-template-settings`, {
            params: CmsObjectHelper.objToSearchParams(params)
        });
    }

    /**
     * @param {Object} params
     * @param {String} params.key
     * @param {String} params.appTemplateId
     * @param {Object} params.search
     * @param {String} params.search.extend - extend app setting with models (app)
     * @return {Observable<Response>}
     */
    getAppTemplateSetting(params?: {settingId: string, key: string, search: {extend: string}}):
        Observable<{appTemplateSetting: AppTemplateSetting}> {
        return this.http.get<{appTemplateSetting: AppTemplateSetting}>(
            `${this.config.gravityCmsApi}/cms/app-template-settings/${params.settingId}`, {
                params: CmsObjectHelper.objToSearchParams(params.search)
            });
    }

    getAppTemplateSettingHistory(appTemplateSettingId, params: Object):
        Observable<{appTemplateSettingHistory: Array<AppTemplateSettingHistory>, pagination: Pagination}> {

        const url = `${this.config.gravityCmsApi}/cms/app-template-settings/${appTemplateSettingId}/history`;

        return this.http.get<{appTemplateSettingHistory: Array<AppTemplateSettingHistory>, pagination: Pagination}>(
            url, {
            params: CmsObjectHelper.objToSearchParams(params)
        });
    }

    rollbackAppTemplateSetting(appTemplateSettingId, appTemplateSettingHistoryId, applyValue) {
        // tslint:disable-next-line:max-line-length
        const url = `${this.config.gravityCmsApi}/cms/app-template-settings/${appTemplateSettingId}/history/${appTemplateSettingHistoryId}/rollback`;

        return this.http.put<any>(url, applyValue);
    }

    /**
     * @param {Object} params
     * @param {String} params.key
     * @param {String} params.appTemplateId
     * @param {String} params.value
     * @param {String} params.type
     * @param {Object} params.search
     * @param {String} params.search.extend - extend app setting with models (app)
     * @return {Observable<Response>}
     */
    updateAppTemplateSetting(params) {

        decodeAppTemplateSetting(params);

        return this.http.put(`${this.config.gravityCmsApi}/cms/app-template-settings/${params.settingId}`,
            {
                value: params.value,
                type: params.type
            }
        );
    }

    /**
     * @param {Object} params
     * @param {String} params.key
     * @param {String} params.appTemplateId
     * @param {String} params.value
     * @param {String} params.type
     * @param {Object} params.search
     * @param {String} params.search.extend - extend app setting with models (app template)
     * @return {Observable<Response>}
     */
    createAppTemplateSetting(params) {

        decodeAppTemplateSetting(params);

        return this.http.post(`${this.config.gravityCmsApi}/cms/app-template-settings`,
            {
                key: params.key,
                value: params.value,
                type: params.type,
                appTemplate: params.appTemplate
            }
        );
    }

    /**
     * @param {Number} appTemplateSettingId
     * @return {Observable<Response>}
     */
    archiveTemplateSetting(appTemplateSettingId) {
        return this.http.put(`${this.config.gravityCmsApi}/cms/app-template-settings/${appTemplateSettingId}/archive`, {});
    }
}
